import { useEffect, useRef, useState } from "react";
import { MapPin, MagnifyingGlass, X, Buildings } from "@phosphor-icons/react";
import { useKnownAddresses, matchKnownAddresses } from "@/lib/knownAddresses";

/**
 * Mapbox-powered US address type-ahead. Ships selected {label, lat, lng} via
 * `onSelect`. Parent still owns the input value so validation and GPS fill
 * keep working — this component is transparent when the user hasn't picked
 * a suggestion (parent can save raw text; backend will re-geocode).
 *
 * Kills bad addresses at the source: techs pick from a verified list instead
 * of typing "1629 Hikcs St" and hoping Mapbox understands it later.
 */
export default function AddressAutocomplete({
  value,
  onChange,             // (text) => void — plain text edits
  onSelect,             // ({label, lat, lng}) => void — when a suggestion is clicked
  placeholder = "Start typing an address…",
  proximity = "-75.1652,39.9526",  // Philadelphia by default; parent overrides for other metros
  bbox,                            // optional "minLon,minLat,maxLon,maxLat" hard-limit
  testId = "address-autocomplete",
  required = false,
  className = "",
  rightSlot = null,               // e.g. the "Use My Location" button
}) {
  const token = process.env.REACT_APP_MAPBOX_TOKEN;
  const known = useKnownAddresses();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSelected, setHasSelected] = useState(false);
  const abortRef = useRef(null);
  const timerRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    // Close the dropdown on outside click
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    // Skip lookup if the user just clicked a suggestion — otherwise every
    // keystroke after a valid pick would re-fetch and re-open the list.
    if (hasSelected) return;
    if (!token || (value || "").trim().length < 3) {
      setItems([]);
      setOpen(false);
      return;
    }
    if (timerRef.current) window.clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    timerRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          q: value,
          access_token: token,
          country: "us",
          types: "address",
          autocomplete: "true",
          permanent: "false",
          limit: "5",
          language: "en",
          proximity,
        });
        if (bbox) params.set("bbox", bbox);
        const r = await fetch(
          `https://api.mapbox.com/search/geocode/v6/forward?${params}`,
          { signal: controller.signal },
        );
        if (!r.ok) throw new Error(`Mapbox ${r.status}`);
        const data = await r.json();
        setItems(data.features || []);
        setOpen(true);
      } catch (e) {
        if (e.name !== "AbortError") setItems([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [value, token, proximity, bbox, hasSelected]);

  const knownHits = hasSelected ? [] : matchKnownAddresses(known, value);
  const showList = open || knownHits.length > 0;

  const pickKnown = (label) => {
    setHasSelected(true);
    setOpen(false);
    setItems([]);
    onChange?.(label);
    onSelect?.({ label, known: true });
  };

  const handleType = (e) => {
    setHasSelected(false);
    onChange?.(e.target.value);
  };

  const pick = (feature) => {
    const coords = feature.geometry?.coordinates || [];
    if (coords.length !== 2) return;
    const [lng, lat] = coords;
    const label =
      feature.properties?.full_address ||
      feature.properties?.place_formatted ||
      feature.place_name ||
      "";
    setHasSelected(true);
    setOpen(false);
    setItems([]);
    onChange?.(label);
    onSelect?.({ label, lat, lng, mapbox_id: feature.properties?.mapbox_id });
  };

  const clear = () => {
    setHasSelected(false);
    setItems([]);
    setOpen(false);
    onChange?.("");
  };

  return (
    <div ref={containerRef} className={`relative ${className}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <div className="flex-1" />
        {rightSlot}
      </div>
      <div className="relative">
        <MagnifyingGlass
          weight="bold"
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
        />
        <input
          type="text"
          value={value || ""}
          onChange={handleType}
          onFocus={() => items.length > 0 && setOpen(true)}
          placeholder={placeholder}
          required={required}
          data-testid={`${testId}-input`}
          className="w-full h-11 pl-9 pr-9 border-2 border-[#0f172a] text-sm font-medium"
        />
        {value && (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear address"
            data-testid={`${testId}-clear`}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-900"
          >
            <X weight="bold" size={14} />
          </button>
        )}
      </div>
      {showList && (items.length > 0 || loading || knownHits.length > 0) && (
        <div
          className="absolute z-[80] mt-1 w-full bg-white border-2 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] max-h-72 overflow-auto"
          data-testid={`${testId}-list`}
        >
          {knownHits.map((a, i) => (
            <button type="button" key={`k-${a}`} onClick={() => pickKnown(a)} data-testid={`${testId}-known-${i}`}
              className="w-full text-left px-3 py-2.5 text-sm font-bold hover:bg-emerald-50 border-b border-slate-200 flex items-start gap-2 bg-emerald-50/40">
              <Buildings weight="fill" size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>{a}</span>
              <span className="ml-auto text-[9px] font-black uppercase tracking-widest text-emerald-700">Your property</span>
            </button>
          ))}
          {loading && items.length === 0 && knownHits.length === 0 && (
            <div className="p-3 text-xs font-bold uppercase tracking-widest text-slate-400">
              Searching Mapbox…
            </div>
          )}
          {items.map((f, i) => {
            const label =
              f.properties?.full_address ||
              f.properties?.place_formatted ||
              f.place_name ||
              "";
            return (
              <button
                type="button"
                key={f.properties?.mapbox_id ?? i}
                onClick={() => pick(f)}
                data-testid={`${testId}-item-${i}`}
                className="w-full text-left px-3 py-2.5 text-sm font-medium hover:bg-amber-50 border-b border-slate-200 last:border-b-0 flex items-start gap-2"
              >
                <MapPin weight="fill" size={14} className="text-[#00C4CC] mt-0.5 flex-shrink-0" />
                <span>{label}</span>
              </button>
            );
          })}
          {!loading && items.length === 0 && knownHits.length === 0 && (
            <div className="p-3 text-xs text-slate-400">No matches — keep typing.</div>
          )}
        </div>
      )}
    </div>
  );
}
